// hooks/usePendingApprovals.js
//
// The App Access nav badge: how many join requests are waiting in the
// Approval Queue. Ported from the badge logic in legacy NavMenu.razor
// (~82–162 + RefreshPendingApprovalCount) — which was duplicated into
// GroupMenu.razor; this hook is the single copy the retrofit doc asks for.
//
// REFRESH SEMANTICS (replicate exactly — retrofit doc §3/§9.7):
//   • refreshed on org/auth change and on EVERY route navigation
//   • deliberately NOT on a timer — no polling anywhere in this feature
//   • non-admins (and no org) read 0 without a request
//   • every fetch error is swallowed silently: the badge keeps its last
//     value; a broken count must never toast at someone browsing the portal
'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { accessPost, GRANT_STATUS } from '@/utils/appAccess';

export default function usePendingApprovals() {
    const pathname = usePathname();
    const { authKey, currentOrg } = useAuth() || {};

    const orgId = currentOrg?.orgId || currentOrg?.OrgId || '';
    const isAdmin = !!currentOrg?.isAdmin;

    const [count, setCount] = useState(0);

    // Stale responses must not clobber a newer org's count — tag each fetch.
    const fetchSeq = useRef(0);

    useEffect(() => {
        if (!authKey || !orgId || !isAdmin) {
            setCount(0);
            return;
        }

        const seq = ++fetchSeq.current;
        (async () => {
            const { ok, data } = await accessPost('GetAccessGrants',
                { Key: authKey, OrgId: orgId, Status: GRANT_STATUS.Pending },
                { action: 'pendingApprovalsBadge' });
            if (!ok) return;                      // swallowed — keep last value
            if (fetchSeq.current !== seq) return; // a newer refresh superseded this
            setCount((data.grants || []).length);
        })();
    }, [authKey, orgId, isAdmin, pathname]);

    return count;
}
